import { useState } from "react";
import { Share2, MessageCircle, Mail, Copy, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";

export const InviteButton = ({ mosque, slug }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const link = `${window.location.origin}/${slug}/mosques/${mosque.id}`;
  const text = t("invite_message").replace("{name}", mosque.name);
  const full = `${text} ${link}`;

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: mosque.name, text, url: link });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      toast.success(t("link_copied"));
      setOpen(false);
    } catch {
      toast.error(t("error_generic"));
    }
  };

  const options = [
    { icon: MessageCircle, label: "WhatsApp", testId: "invite-whatsapp", href: `https://wa.me/?text=${encodeURIComponent(full)}` },
    { icon: Smartphone, label: "SMS", testId: "invite-sms", href: `sms:?&body=${encodeURIComponent(full)}` },
    { icon: Mail, label: "Email", testId: "invite-email", href: `mailto:?subject=${encodeURIComponent(mosque.name)}&body=${encodeURIComponent(full)}` },
  ];

  return (
    <>
      <Button data-testid="mosque-invite-btn" onClick={share} variant="outline" className="rounded-full px-5 active:scale-95 shrink-0">
        <Share2 className="h-4 w-4 mr-2" /> {t("invite_members")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("invite_members")}</DialogTitle>
            <DialogDescription className="text-sm">{t("invite_dialog_text").replace("{name}", mosque.name)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {options.map((o) => (
              <a
                key={o.testId}
                data-testid={o.testId}
                href={o.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 border border-border rounded-lg px-4 py-3 text-sm font-medium hover:bg-muted transition-colors"
              >
                <o.icon className="h-4 w-4 text-accent" /> {o.label}
              </a>
            ))}
            <button
              data-testid="invite-copy-link"
              onClick={copy}
              className="w-full flex items-center gap-3 border border-border rounded-lg px-4 py-3 text-sm font-medium hover:bg-muted transition-colors"
            >
              <Copy className="h-4 w-4 text-accent" /> {t("copy_link")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
